import { Alert, Button, Spin } from 'antd';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { sessionQueryOptions } from '@/features/auth/queries/session-query';
import { getApiErrorMessage } from '@/shared/api/api-error';

export function RequireSession() {
  const location = useLocation();
  const session = useQuery(sessionQueryOptions);

  if (session.isPending) {
    return (
      <div
        className="full-page-status"
        role="status"
        aria-label="Loading session"
      >
        <Spin size="large" />
      </div>
    );
  }

  if (session.isError) {
    const status = axios.isAxiosError(session.error)
      ? session.error.response?.status
      : undefined;
    const sessionRejected = status === 401 || status === 403;

    // A timeout or temporary network failure must not destroy a valid cached
    // session and throw the user back to the login page.
    if (session.data && !sessionRejected) {
      return <Outlet context={session.data} />;
    }

    if (!sessionRejected) {
      return (
        <div className="full-page-status" role="alert">
          <Alert
            showIcon
            type="error"
            title="The session could not be checked"
            description={getApiErrorMessage(session.error)}
            action={
              <Button
                onClick={() => {
                  void session.refetch();
                }}
              >
                Try again
              </Button>
            }
          />
        </div>
      );
    }

    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <Outlet context={session.data} />;
}
